import React, { useState, useEffect } from "react";
import { Heart, ShoppingCart, Share2, Trash2, Loader2 } from "lucide-react";
import { 
  getFirestore, 
  collection, 
  query, 
  where, 
  getDocs, 
  deleteDoc, 
  doc,
  addDoc 
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { useNavigate } from "react-router-dom";

const Wishlist = () => {
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const db = getFirestore();
  const auth = getAuth();
  const user = auth.currentUser;

  useEffect(() => {
    if (user) {
      fetchWishlistItems();
    } else {
      setLoading(false);
      setError("Please sign in to view your wishlist");
    }
  }, [user]);

  const fetchWishlistItems = async () => {
    try {
      setLoading(true);
      const wishlistRef = collection(db, `users/${user.uid}/wishlist`);
      const q = query(wishlistRef);
      const querySnapshot = await getDocs(q);
      
      const items = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setWishlistItems(items);
    } catch (error) {
      console.error('Error fetching wishlist:', error);
      setError("Failed to load wishlist items");
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async (item) => {
    try {
      // Add to cart collection
      const cartRef = collection(db, `users/${user.uid}/cart`);
      await addDoc(cartRef, {
        productId: item.productId,
        name: item.name,
        price: item.price,
        images: [item.image], // Assuming image is stored as URL
        quantity: 1,
        addedAt: new Date().toISOString()
      });

      // Optionally remove from wishlist after adding to cart
      await handleRemoveFromWishlist(item.id);

      alert('Item added to cart successfully!');
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('Failed to add item to cart');
    }
  };

  const handleRemoveFromWishlist = async (wishlistItemId) => {
    try {
      await deleteDoc(doc(db, 'wishlist', wishlistItemId));
      // Update local state
      setWishlistItems(prev => prev.filter(item => item.id !== wishlistItemId));
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      alert('Failed to remove item from wishlist');
    }
  };

  const handleShareItem = (item) => {
    // You can implement sharing functionality here
    // For example, using the Web Share API
    if (navigator.share) {
      navigator.share({
        title: item.name,
        text: `Check out this ${item.name} on Ambaari Jewels!`,
        url: window.location.href
      });
    } else {
      alert('Sharing is not supported on this device');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <p className="text-red-500">{error}</p>
        {!user && (
          <button 
            onClick={() => navigate("/login")}
            className="px-6 py-2 bg-purple-700 text-white rounded-lg hover:bg-purple-600 transition-all duration-200"
          >
            Sign In
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-2xl font-medium text-purple-900">My Wishlist</h3>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-purple-600">
            {wishlistItems.length} items
          </span>
        </div>
      </div>

      {wishlistItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 space-y-4">
          <Heart className="w-16 h-16 text-purple-300" />
          <p className="text-purple-500 text-lg">Your wishlist is empty</p>
          <button 
            onClick={() => navigate("/shop")}
            className="px-6 py-2 bg-purple-700 text-white rounded-lg hover:bg-purple-600 transition-all duration-200 transform hover:scale-105 shadow-md"
          >
            Continue Shopping
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {wishlistItems.map((item) => (
            <div
              key={item.id}
              className="bg-white border border-purple-200 rounded-lg p-6 hover:shadow-md transition-all duration-200"
            >
              <div className="flex items-center space-x-6">
                <img
                  src={item.images[0]}
                  alt={item.name}
                  className="w-24 h-24 rounded-lg object-cover"
                />

                <div className="flex-grow space-y-2">
                  <h4 className="font-medium text-purple-900">{item.name}</h4>
                  <div className="flex items-center space-x-2">
                    <span className="text-lg font-medium text-purple-900">
                      ₹{item.price.toLocaleString()}
                    </span>
                  </div>
                  <div className="text-sm text-purple-600">
                    Added on {new Date(item.addedAt).toLocaleDateString()}
                  </div>
                </div>

                <div className="flex flex-col space-y-2">
                  <button
                    onClick={() => handleAddToCart(item)}
                    className="flex items-center justify-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium
                      bg-purple-700 text-white hover:bg-purple-600 shadow-md
                      transition-all duration-200 transform hover:scale-105"
                  >
                    <ShoppingCart className="w-4 h-4" />
                    <span>Add to Cart</span>
                  </button>

                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleShareItem(item)}
                      className="p-2 text-purple-600 hover:text-purple-900 transition-colors duration-200"
                    >
                      <Share2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleRemoveFromWishlist(item.id)}
                      className="p-2 text-purple-600 hover:text-red-600 transition-colors duration-200"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Decorative pattern overlay */}
      <div
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{
          backgroundImage: `
            linear-gradient(to right, rgba(147, 51, 234, 0.05) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(147, 51, 234, 0.05) 1px, transparent 1px)
          `,
          backgroundSize: "50px 50px",
        }}
      />
    </div>
  );
};

export default Wishlist;